/**
 * Version management — pure helpers, verbatim from source
 * (defaultVersions / versionsFor / selectedVersion / publish rev math).
 */
import type { Adapter, Integration, Version } from './types';
import { INTS } from '../data/seeds';

/** Build the default version list for a connector id (catalog vs custom). */
export function defaultVersions(id: string, adapters: Adapter[]): Version[] {
  const int = INTS.find(x => x.id === id);
  const a = adapters.find(x => x.id === id);
  if (int) {
    return int.versions.map((v, i) => ({
      id: id + '|' + v,
      catalog: 'v1',
      vendor: v,
      status: i === 0 ? 'published' : 'deprecated',
      tag: i === 1 ? 'N-1' : null,
      rev: i === 0 ? 3 : 1,
      execs: i === 0 ? 9000 + ((id.length * 137) % 8000) : 2400,
    }));
  }
  const v = (a && a.version) || '1.0';
  return [
    {
      id: id + '|' + v,
      catalog: 'v1',
      vendor: v,
      status: a && a.approved ? 'published' : 'draft',
      tag: null,
      rev: a && a.approved ? 1 : 0,
      execs: 0,
    },
  ];
}

/** Versions for an id — explicit state override, else defaults. */
export function versionsFor(
  id: string,
  versions: Record<string, Version[]>,
  adapters: Adapter[],
): Version[] {
  return versions[id] || defaultVersions(id, adapters);
}

/** The selected version id for a connector (validated against the list). */
export function selectedVersionId(
  id: string,
  versions: Record<string, Version[]>,
  adapters: Adapter[],
  versionSel: Record<string, string>,
): string | undefined {
  const vs = versionsFor(id, versions, adapters);
  const sel = versionSel[id];
  return sel && vs.some(v => v.id === sel) ? sel : vs[0] && vs[0].id;
}

/** Bump rev on the published version (config-change side effect). */
export function bumpRevList(vs: Version[]): Version[] {
  return vs.map(v => (v.status === 'published' ? { ...v, rev: (v.rev || 0) + 1 } : v));
}

/** Publish the selected version: it → published(rev+1), prior published → N-1. */
export function publishList(vs: Version[], selId: string | undefined): Version[] {
  return vs.map(v =>
    v.id === selId
      ? { ...v, status: 'published' as const, tag: null, rev: (v.rev || 0) + 1 }
      : v.status === 'published'
      ? { ...v, tag: 'N-1' }
      : v,
  );
}

/** Source for activeAdapter() fallback when an INTS connector is opened raw. */
export function adapterFromInt(int: Integration): Adapter {
  return {
    id: int.id,
    name: int.name,
    initials: int.initials,
    color: int.color,
    cls: int.cls,
    source: 'mapping · catalog v1',
    version: (int.versions && int.versions[0]) || '1.0',
    company: int.company,
    category: int.category,
    approved: true,
  };
}
